import { useEffect, useState } from "react";
import { toast } from "sonner";
import { MagnifyingGlass, Crown, Clock, ArrowCounterClockwise } from "@phosphor-icons/react";
import { adminUsersApi, adminUserApi, adminGrantProApi, adminExtendTrialApi, adminRefundApi, formatApiErrorDetail } from "@/lib/api";
import { money, shortDate } from "@/lib/format";

const err = (e) => toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Something went wrong");
const status = (u) => (u.plan_status === "active" ? `${u.plan === "team" ? "Team" : "Pro"}${u.plan_interval && u.plan_interval !== "monthly" ? ` · ${u.plan_interval}` : ""}` : u.plan_status === "trialing" ? `Trial · ${u.trial_days_left}d` : "Expired");

function DaysAction({ label, icon: Icon, onRun, testId, presets }) {
  const [days, setDays] = useState(presets[0]);
  return (
    <div className="rounded-2xl bg-[#FAFAFC] p-4">
      <p className="text-sm font-semibold text-ink">{label}</p>
      <div className="mt-3 flex flex-wrap items-center gap-2">
        {presets.map((d) => (
          <button key={d} data-testid={`${testId}-preset-${d}`} onClick={() => setDays(d)} className={`h-8 rounded-full px-3 text-xs font-medium ${days === d ? "bg-ink text-white" : "bg-white text-ink shadow-[inset_0_0_0_1px_rgba(0,0,0,0.08)]"}`}>{d}d</button>
        ))}
        <input data-testid={`${testId}-days-input`} type="number" min={1} value={days} onChange={(e) => setDays(Number(e.target.value))} className="h-8 w-16 rounded-full border border-black/10 px-2 text-center text-xs" />
        <button data-testid={`${testId}-button`} onClick={() => onRun(days)} className="ml-auto flex h-8 items-center gap-1 rounded-full bg-brand px-3.5 text-xs font-semibold text-white hover:bg-brand-hover"><Icon size={13} weight="fill" /> Apply</button>
      </div>
    </div>
  );
}

function UserDetail({ id, onChanged }) {
  const [d, setD] = useState(null);
  useEffect(() => { adminUserApi(id).then(setD).catch(err); }, [id]);
  if (!d) return <p className="text-sm text-mist">Loading…</p>;
  const u = d.user;
  const run = async (fn, msg) => { try { const r = await fn(); if (r?.user) setD(r); else setD(await adminUserApi(id)); toast.success(msg); onChanged(); } catch (e) { err(e); } };
  const refund = async (p) => {
    if (!window.confirm(`Refund ${money(p.amount, p.currency)} to ${u.email}? Their plan will end now.`)) return;
    try { const r = await adminRefundApi(p.session_id); toast.success(r.note); setD(await adminUserApi(id)); onChanged(); } catch (e) { err(e); }
  };
  return (
    <div className="space-y-5" data-testid="user-detail">
      <div>
        <p className="font-display text-xl font-semibold text-ink">{u.name || u.email}</p>
        <p className="text-sm text-mist">{u.email} · joined {shortDate(u.created_at)} · {u.auth_provider}</p>
        <p data-testid="user-detail-status" className="mt-2 inline-flex rounded-full bg-brand-soft px-3 py-1 text-xs font-semibold text-brand">{status(u)}{u.plan_status === "active" ? ` · until ${shortDate(u.plan_expires_at)}` : ""}</p>
      </div>
      <DaysAction label="Give free Pro time" icon={Crown} testId="grant-pro" presets={[7, 30, 90]} onRun={(days) => run(() => adminGrantProApi(id, days), `Gave ${days} days of Pro`)} />
      <DaysAction label="Extend free trial" icon={Clock} testId="extend-trial" presets={[7, 14, 30]} onRun={(days) => run(() => adminExtendTrialApi(id, days), `Trial extended by ${days} days`)} />
      <div>
        <p className="mb-2 text-sm font-semibold text-ink">Payments</p>
        {d.payments.length === 0 ? <p className="text-xs text-mist" data-testid="user-payments-empty">No payments</p> : (
          <ul className="divide-y divide-black/5" data-testid="user-payments">
            {d.payments.map((p) => (
              <li key={p.session_id} className="flex items-center justify-between gap-2 py-2.5 text-sm" data-testid="user-payment-row">
                <span><span className="font-medium text-ink">{money(p.amount, p.currency)}</span> <span className="text-xs text-mist">· {p.package_id} {p.interval} · {p.provider} · {shortDate(p.updated_at)}</span></span>
                {p.payment_status === "refunded"
                  ? <span className="rounded-full bg-black/5 px-2.5 py-1 text-[11px] text-mist" data-testid="payment-refunded-badge">Refunded</span>
                  : <button data-testid="refund-button" onClick={() => refund(p)} className="flex items-center gap-1 rounded-full bg-[#FFECEE] px-3 py-1 text-xs font-semibold text-[#D70015] hover:bg-[#FFDDE1]"><ArrowCounterClockwise size={12} /> Refund</button>}
              </li>
            ))}
          </ul>
        )}
      </div>
      {d.audit.length > 0 && (
        <div>
          <p className="mb-1 text-xs font-semibold text-mist">History</p>
          {d.audit.map((a) => <p key={a.id} className="text-[11px] text-mist">{shortDate(a.created_at)} · {a.admin} · {a.action.replace("_", " ")} {a.detail}</p>)}
        </div>
      )}
    </div>
  );
}

export function UsersPanel() {
  const [q, setQ] = useState("");
  const [list, setList] = useState([]);
  const [sel, setSel] = useState(null);
  const search = () => adminUsersApi(q).then(setList).catch(err);
  useEffect(() => { const t = setTimeout(search, 250); return () => clearTimeout(t); }, [q]); // eslint-disable-line

  return (
    <div className="grid grid-cols-1 gap-6 lg:grid-cols-[400px_1fr]" data-testid="users-panel">
      <div className="stp-card rounded-3xl p-3">
        <div className="flex items-center gap-2 rounded-full bg-[#FAFAFC] px-4">
          <MagnifyingGlass size={16} className="text-mist" />
          <input data-testid="user-search-input" value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search by email or name" className="h-11 flex-1 bg-transparent text-sm text-ink outline-none" />
        </div>
        <div className="mt-2 max-h-[65vh] overflow-y-auto">
          {list.length === 0 ? <p className="p-6 text-center text-sm text-mist">No users found</p> : list.map((u) => (
            <button key={u.user_id} data-testid="user-row" onClick={() => setSel(u.user_id)} className={`w-full rounded-2xl p-3 text-left ${sel === u.user_id ? "bg-brand-soft" : "hover:bg-black/[0.03]"}`}>
              <div className="flex items-center justify-between gap-2">
                <span className="truncate text-sm font-medium text-ink">{u.email}</span>
                <span className="shrink-0 text-[11px] text-mist">{status(u)}</span>
              </div>
              <p className="text-[11px] text-mist">{u.name}{u.is_admin ? " · admin" : ""}</p>
            </button>
          ))}
        </div>
      </div>
      <div className="stp-card min-h-[50vh] rounded-3xl p-6 sm:p-8">
        {sel ? <UserDetail key={sel} id={sel} onChanged={search} /> : <p className="text-sm text-mist">Select a user to manage their plan, trial and payments</p>}
      </div>
    </div>
  );
}
